import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { broadcastScheduleQueryKeys } from '@/features/broadcast-schedule/api/query-keys';
import { TSegment } from '@/features/segments/types';

import { useBroadcastScheduleProgram } from '../context/BroadcastScheduleProgramContext';

type UseActiveOptionSegmentsEditsParams = {
  id: string;
  programSegmentsData: TSegment[] | null;
  /** Whether the list's answer has landed in the view on screen. */
  listIsSettled: boolean;
};

/**
 * Marks the unfiltered segments behind the filter options stale after a reviewer's in-place
 * edit, so the next opening under a filter fetches them again. Never reads nor writes them.
 */
export function useActiveOptionSegmentsEdits({
  id,
  programSegmentsData,
  listIsSettled,
}: UseActiveOptionSegmentsEditsParams) {
  const queryClient = useQueryClient();
  const { segmentItems, globalViewSegments } = useBroadcastScheduleProgram();
  const checkedSegmentItems = useRef(segmentItems);
  const checkedGlobalViewSegments = useRef(globalViewSegments);

  useEffect(() => {
    if (!listIsSettled || !programSegmentsData) return;

    // RISK: evaluated per collection against its own previous check, never in the render where
    // `programSegmentsData` changes. Both copies of the data land one render after it, so
    // comparing them to it there would read every reload as an edit. Empty arrays are never
    // edits either: the global view gets one while the list reloads and on unmount.
    const changed = [
      segmentItems !== checkedSegmentItems.current && segmentItems,
      globalViewSegments !== checkedGlobalViewSegments.current && globalViewSegments,
    ];
    checkedSegmentItems.current = segmentItems;
    checkedGlobalViewSegments.current = globalViewSegments;

    const edited = changed.some(
      collection => collection && collection.length > 0 && collection !== programSegmentsData
    );
    if (edited) {
      queryClient.invalidateQueries({
        queryKey: broadcastScheduleQueryKeys.activeOptionSegments(id),
        refetchType: 'none',
      });
    }
  }, [segmentItems, globalViewSegments, programSegmentsData, listIsSettled]);

  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: broadcastScheduleQueryKeys.activeOptionSegments(id),
        exact: true,
      });
    },
    [id]
  );
}
